import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { FamOnboarding } from '../api/endpoints/famOnboardingApi';
import { FAMOnboarding } from "../models/membership/famOnboarding";

interface MembershipOnBoardingState {
    famOnboardingDetails: FAMOnboarding[] | null;
    famOnboardingDetailsById: FAMOnboarding[] | null;
    basketId: string;
    famOnboardingCanPayStatusCodes: string[] | null;

    famOnboardingDetailsStatus: ApiRequestStatus;
    getFamOnboardingDetailsByIdStatus: ApiRequestStatus;
    updateFamOnboardingDetailsStatus: ApiRequestStatus;
    submitFamOnboardingDetailsStatus: ApiRequestStatus;
    basketIdStatus: ApiRequestStatus;
    famOnboardingCanPayStatusCodesStatus: ApiRequestStatus;
}

const initialState: MembershipOnBoardingState = {
    famOnboardingDetails: null,
    famOnboardingDetailsById: null,
    basketId: '',
    famOnboardingCanPayStatusCodes: null,

    famOnboardingDetailsStatus: ApiRequestStatus.Idle,
    getFamOnboardingDetailsByIdStatus: ApiRequestStatus.Idle,
    updateFamOnboardingDetailsStatus: ApiRequestStatus.Idle,
    submitFamOnboardingDetailsStatus: ApiRequestStatus.Idle,
    basketIdStatus: ApiRequestStatus.Idle,
    famOnboardingCanPayStatusCodesStatus: ApiRequestStatus.Idle
}

export const getFamOnboardingDetailsAsync = createReadAsyncThunk<FAMOnboarding[]>(
    'famOnboarding/getFamOnboardingDetailsAsync',
    async (_, thunkAPI) => {
        try {
            return await FamOnboarding.getFamOnboardingDetailsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getFamOnboardingCanPayStatusCodesAsync = createReadAsyncThunk<string[]>(
    'famOnboarding/getFamOnboardingCanPayStatusCodesAsync',
    async (_, thunkAPI) => {
        try {
            return await FamOnboarding.getFamOnboardingCanPayStatusCodesAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getFamOnboardingDetailsByIdAsync = createReadAsyncThunk<FAMOnboarding[], string>(
    'famOnboarding/getFamOnboardingDetailsByIdAsync',
    async (id, thunkAPI) => {
        try {
            return await FamOnboarding.getByIdAsync(id);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updateFamOnboardingDetailsAsync = createAsyncThunk<boolean, FAMOnboarding>(
    'famOnboarding/updateFamOnboardingDetailsAsync',
    async (famOnboarding, thunkAPI) => {
        try {
            return await FamOnboarding.updateFamOnboardingDetailsAsync(famOnboarding);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const submitFamOnboardingDetailsAsync = createAsyncThunk<boolean, string>(
    'famOnboarding/submitFamOnboardingDetailsAsync',
    async (famExamId, thunkAPI) => {
        try {
            return await FamOnboarding.submitFamOnboardingDetailsAsync(famExamId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const basketAsync = createAsyncThunk<string, string>(
    'famOnboarding/basketAsync',
    async (famExamId, thunkAPI) => {
        try {
            return await FamOnboarding.basketAsync(famExamId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const famOnboardingSlice = createSlice({
    name: 'famOnboardingSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getFamOnboardingDetailsAsync.pending, (state) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFamOnboardingDetailsAsync.fulfilled, (state, action) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Fulfilled;
            state.famOnboardingDetails = action.payload;
        });
        builder.addCase(getFamOnboardingDetailsAsync.rejected, (state) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFamOnboardingCanPayStatusCodesAsync.pending, (state) => {
            state.famOnboardingCanPayStatusCodesStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFamOnboardingCanPayStatusCodesAsync.fulfilled, (state, action) => {
            state.famOnboardingCanPayStatusCodesStatus = ApiRequestStatus.Fulfilled;
            state.famOnboardingCanPayStatusCodes = action.payload;
        });
        builder.addCase(getFamOnboardingCanPayStatusCodesAsync.rejected, (state) => {
            state.famOnboardingCanPayStatusCodesStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFamOnboardingDetailsByIdAsync.pending, (state) => {
            state.getFamOnboardingDetailsByIdStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFamOnboardingDetailsByIdAsync.fulfilled, (state, action) => {
            state.getFamOnboardingDetailsByIdStatus = ApiRequestStatus.Fulfilled;
            state.famOnboardingDetailsById = action.payload;
        });
        builder.addCase(getFamOnboardingDetailsByIdAsync.rejected, (state) => {
            state.getFamOnboardingDetailsByIdStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(updateFamOnboardingDetailsAsync.pending, (state) => {
            state.updateFamOnboardingDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(updateFamOnboardingDetailsAsync.fulfilled, (state) => {
            state.updateFamOnboardingDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(updateFamOnboardingDetailsAsync.rejected, (state) => {
            state.updateFamOnboardingDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(submitFamOnboardingDetailsAsync.pending, (state) => {
            state.submitFamOnboardingDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(submitFamOnboardingDetailsAsync.fulfilled, (state) => {
            state.submitFamOnboardingDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(submitFamOnboardingDetailsAsync.rejected, (state) => {
            state.submitFamOnboardingDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(basketAsync.pending, (state) => {
            state.basketIdStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(basketAsync.fulfilled, (state, action) => {
            state.basketId = action.payload;
            state.basketIdStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(basketAsync.rejected, (state) => {
            state.basketIdStatus = ApiRequestStatus.Rejected;
        });
    })
})
